import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table,
  Tag,
  Button,
  Modal,
  Drawer,
  Badge,
  Popconfirm,
  Input,
  InputNumber,
  DatePicker,
  message,
  Empty,
  Spin,
  Select,
  Alert,
  Collapse,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  RightOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  fetchMyTrucks,
  fetchMyRequests,
  setTruckAvailable,
  bulkStatus,
  bulkConfirm,
  respondMatch,
  counterMatch,
  declineMatch,
} from "../utils/matchingApi";
import { transportBodyTypes } from "../components/search/categories";

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
  Authorization: `JWT ${window.localStorage.getItem("access")}`,
});

// leaf body types only (children of a group) — those are what trucks carry
const BODY_TYPE_OPTIONS = transportBodyTypes
  .filter((bt) => bt.parent_id != null)
  .map((bt) => ({ value: bt.id, label: bt.name }));

// matching service currency ids (backend: 1 USD, 2 RUB, 3 KZT, 4 KGS);
// order and labels follow the Add_Free_Vehicle wireframe
const CURRENCIES = [
  { value: 2, label: "RUB (₽)", sign: "₽" },
  { value: 4, label: "KGS (сом)", sign: "сом" },
  { value: 1, label: "USD ($)", sign: "$" },
  { value: 3, label: "KZT (₸)", sign: "₸" },
];
const currencySign = (id) => CURRENCIES.find((c) => c.value === id)?.sign || "";

const RADIUS_OPTIONS = [
  { value: 50, label: "50 км" },
  { value: 100, label: "100 км" },
  { value: 200, label: "200 км" },
  { value: 500, label: "500 км" },
  { value: 2000, label: "Любой" }, // 2000 = backend max
];

const BODY_TYPE_NAMES = new Map(
  transportBodyTypes.map((bt) => [bt.id, bt.name]),
);
const bodyTypeName = (id) =>
  (typeof id === "object" ? id?.name : BODY_TYPE_NAMES.get(id)) || "—";

/**
 * «Мой транспорт» (ТЗ §8, §10): fleet table with live status/freshness,
 * checkbox multi-select and bulk actions — PLUS the carrier side of matching:
 * incoming load requests per truck (score %, my decision) with
 * Принять запрос / Предложить цену / Отклонить (ТЗ §4.3, §8).
 *
 * Works both for COMPANY (many trucks) and DRIVER (one truck) accounts.
 * Polls requests every 20s for the «Новые запросы» badge.
 */
// Wireframe statuses: Ищет груз / В рейсе / Освобождается завтра / Стоит
const STATUS_META = {
  searching: { color: "green", label: "Ищет груз" },
  trip: { color: "orange", label: "В рейсе" },
  tomorrow: { color: "blue", label: "Освобождается завтра" },
  idle: { color: "default", label: "Стоит" },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, m]) => ({
  value,
  label: m.label,
}));

const ymd = (d) => {
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};

/** live availability row (or null) → wireframe status key */
const deriveState = (lt) => {
  if (!lt) return "idle";
  if (lt.status === "BUSY") return "trip";
  if (lt.status === "ACTIVE") {
    const tomorrow = ymd(Date.now() + 86_400_000);
    if (lt.available_from && ymd(lt.available_from) === tomorrow) return "tomorrow";
    return "searching";
  }
  return "idle"; // INACTIVE / EXPIRED
};

const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const DECIDED_META = {
  responded: { color: "green", label: "Готов по ставке · ожидает ответа" },
  countered: { color: "blue", label: "Предложена цена" },
  accepted: { color: "green", label: "Запрос принят" },
  closed: { color: "default", label: "Закрыт" },
};

const POLL_INTERVAL_MS = 20_000;

const fmtMoney = (amount, currency) => {
  if (amount == null) return "по договоренности";
  const n = Number(amount).toLocaleString("ru-RU");
  return `${n} ${currency || ""}`.trim();
};

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d.length === 10 ? `${d}T00:00:00` : d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
};

const scoreColor = (score) =>
  score >= 90 ? "#1e8e5a" : score >= 70 ? "#2463eb" : "#b0721a";

const MyFleet = () => {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [requests, setRequests] = useState([]); // /m/truck/matches rows
  const [selected, setSelected] = useState([]);
  const [tab, setTab] = useState("fleet"); // fleet | requests
  const [drawerVehicle, setDrawerVehicle] = useState(null); // vehicle row
  const [busyMatch, setBusyMatch] = useState(null); // match being acted on
  const [counterTarget, setCounterTarget] = useState(null); // match for price modal
  const [counterAmount, setCounterAmount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [liveError, setLiveError] = useState(false);

  // filters + paging (wireframe: статус / тип кузова / город / куда ищет)
  const [fStatus, setFStatus] = useState(null);
  const [fBody, setFBody] = useState(null);
  const [fCity, setFCity] = useState(null);
  const [fDest, setFDest] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // availability modal: mode "add" (publish a free truck) | "update" (existing row)
  const [availModal, setAvailModal] = useState(null); // { mode, vehicle? }
  const [form, setForm] = useState({});
  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  // vehicle editor («Редактировать машину»)
  const [editVehicle, setEditVehicle] = useState(null);
  const [vForm, setVForm] = useState({});
  const [publishResult, setPublishResult] = useState(null); // {count, top[]}

  const [searchParams, setSearchParams] = useSearchParams();

  // ── data loading ──────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await axios
        .get(`${API}/auth/users/me/`, { headers: authHeaders() })
        .catch(() => null);
      const meId = meRes?.data?.id;
      if (!meId) {
        message.warning("Не удалось определить пользователя — обновите страницу");
        setVehicles([]);
        setRequests([]);
        return;
      }
      const [transportsRes, live, feed] = await Promise.allSettled([
        axios.get(`${API}/transports/`, { params: { owner: meId } }),
        fetchMyTrucks(),
        fetchMyRequests(),
      ]).then((r) => r.map((p) => (p.status === "fulfilled" ? p.value : null)));
      setLiveError(!live || !feed);

      const raw = transportsRes?.data;
      const transports = Array.isArray(raw) ? raw : raw?.results || [];
      const mine = transports.filter((t) => t.owner?.id === meId);
      const liveByVehicle = {};
      ((live?.trucks) || []).forEach((lt) => {
        liveByVehicle[lt.vehicle_id] = lt;
      });

      const rows = mine.map((t) => {
        const lt = liveByVehicle[t.id] || null;
        return {
          vehicle_id: t.id,
          mark: t.mark || t.model || "—",
          model: t.model || "",
          body_type_id: typeof t.body_type === "object" ? t.body_type?.id : t.body_type,
          plate: t.government_number || "—",
          body_type: bodyTypeName(t.body_type),
          max_load_weight: t.max_load_weight,
          max_load_volume: t.max_load_volume,
          state: deriveState(lt),
          expired: lt?.status === "EXPIRED",
          status: lt?.status || null, // null = no availability record («Стоит»)
          country: lt?.country || null,
          currency: lt?.currency ?? null,
          radius_km: lt?.radius_km ?? null,
          available_until: lt?.available_until || null,
          city: lt?.city || null,
          destinations: lt?.destinations || [],
          min_price: lt?.min_price ?? null,
          confirmed_minutes_ago: lt?.confirmed_minutes_ago,
          available_from: lt?.available_from || null,
          live: Boolean(lt),
        };
      });
      setVehicles(rows);
      setRequests(feed?.matches || []);
    } catch (e) {
      message.error("Не удалось загрузить автопарк");
    } finally {
      setLoading(false);
    }
  }, []);

  // Light refresh for polling: live trucks + requests only (no Django re-fetch).
  const refreshLive = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    try {
      const [live, feed] = await Promise.allSettled([
        fetchMyTrucks(),
        fetchMyRequests(),
      ]).then((r) => r.map((p) => (p.status === "fulfilled" ? p.value : null)));
      const liveByVehicle = {};
      ((live?.trucks) || []).forEach((lt) => {
        liveByVehicle[lt.vehicle_id] = lt;
      });
      setVehicles((prev) =>
        prev.map((v) => {
          const lt = liveByVehicle[v.vehicle_id] || null;
          return lt
            ? {
                ...v,
                state: deriveState(lt),
                expired: lt.status === "EXPIRED",
                status: lt.status,
                country: lt.country || null,
                currency: lt.currency ?? null,
                radius_km: lt.radius_km ?? null,
                available_until: lt.available_until || null,
                city: lt.city,
                destinations: lt.destinations,
                min_price: lt.min_price ?? null,
                confirmed_minutes_ago: lt.confirmed_minutes_ago,
                available_from: lt.available_from,
                live: true,
              }
            : { ...v, state: "idle", expired: false, status: null, city: null, destinations: [], min_price: null, confirmed_minutes_ago: null, live: false };
        }),
      );
      setRequests(feed?.matches || []);
    } catch {
      /* matching service down — keep last state */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Phase 4: poll for new requests while the page is open
  const pollRef = useRef(null);
  useEffect(() => {
    pollRef.current = setInterval(refreshLive, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [refreshLive]);

  // ── derived: stats + per-vehicle request counts ───────────────────────
  const requestsByVehicle = React.useMemo(() => {
    const map = new Map();
    for (const r of requests) {
      const list = map.get(r.vehicle_id) || [];
      list.push(r);
      map.set(r.vehicle_id, list);
    }
    return map;
  }, [requests]);

  const newCount = React.useMemo(
    () => requests.filter((r) => !r.decided).length,
    [requests],
  );

  const stats = {
    total: vehicles.length,
    active: vehicles.filter((v) => v.state === "searching").length,
    busy: vehicles.filter((v) => v.state === "trip").length,
    tomorrow: vehicles.filter((v) => v.state === "tomorrow").length,
    newRequests: newCount,
  };

  const filtered = React.useMemo(
    () =>
      vehicles.filter(
        (v) =>
          (!fStatus || v.state === fStatus) &&
          (!fBody || v.body_type_id === fBody) &&
          (!fCity || v.city === fCity) &&
          (!fDest || (v.destinations || []).some((d) => d.city === fDest)),
      ),
    [vehicles, fStatus, fBody, fCity, fDest],
  );
  const cityOptions = [...new Set(vehicles.map((v) => v.city).filter(Boolean))].map(
    (c) => ({ value: c, label: c }),
  );
  const destOptions = [
    ...new Set(vehicles.flatMap((v) => (v.destinations || []).map((d) => d.city))),
  ].map((c) => ({ value: c, label: c }));
  const hasFilters = fStatus || fBody || fCity || fDest;
  const resetFilters = () => {
    setFStatus(null);
    setFBody(null);
    setFCity(null);
    setFDest(null);
    setPage(1);
  };

  // ── actions ───────────────────────────────────────────────────────────
  const onBulkStatus = async (status) => {
    setBusy(true);
    try {
      const res = await bulkStatus(selected, status);
      message.success(`Обновлено машин: ${res.updated}`);
      setSelected([]);
      load();
    } catch (e) {
      message.error(e?.message || "Ошибка обновления");
    } finally {
      setBusy(false);
    }
  };

  const onBulkConfirm = async () => {
    setBusy(true);
    try {
      const res = await bulkConfirm(selected);
      message.success(`Подтверждено машин: ${res.confirmed}`);
      setSelected([]);
      load();
    } catch (e) {
      message.error(e?.message || "Ошибка подтверждения");
    } finally {
      setBusy(false);
    }
  };

  const onAcceptRequest = async (m) => {
    setBusyMatch(m.match_id);
    try {
      await respondMatch(m.match_id);
      message.success("Запрос принят — ожидайте ответа заказчика");
      await refreshLive();
    } catch (e) {
      message.error(e?.message || "Не удалось принять запрос");
    } finally {
      setBusyMatch(null);
    }
  };

  const openCounter = (m) => {
    setCounterTarget(m);
    setCounterAmount(m.load?.price ?? null);
  };

  const onCounter = async () => {
    if (!counterAmount || counterAmount <= 0) {
      message.warning("Укажите ставку");
      return;
    }
    setBusyMatch(counterTarget.match_id);
    try {
      await counterMatch(counterTarget.match_id, counterAmount);
      message.success("Цена предложена");
      setCounterTarget(null);
      await refreshLive();
    } catch (e) {
      message.error(e?.message || "Не удалось предложить цену");
    } finally {
      setBusyMatch(null);
    }
  };

  const onDecline = async (m) => {
    setBusyMatch(m.match_id);
    try {
      await declineMatch(m.match_id);
      message.success("Запрос отклонён");
      await refreshLive();
    } catch (e) {
      message.error(e?.message || "Не удалось отклонить");
    } finally {
      setBusyMatch(null);
    }
  };

  const emptyForm = {
    city: "",
    radius_km: 200,
    from: null,
    until: null,
    dests: [],
    minRate: null,
    currency: 2,
    comment: "",
  };

  const openAdd = () => {
    if (vehicles.length === 0) {
      // permanent data first: a truck must exist before it can be published
      openCreateVehicle();
      return;
    }
    setForm({ ...emptyForm, vehicle_id: null });
    setAvailModal({ mode: "add" });
  };

  // bulk «Обновить состояние» for the selected trucks (spec §10, one form → N trucks)
  const openBulk = () => {
    setForm({ ...emptyForm, vehicle_id: null });
    setAvailModal({ mode: "bulk", ids: selected });
  };

  const openCreateVehicle = () => {
    setVForm({});
    setEditVehicle({ create: true });
  };

  // deep link from «Создать транспорт» (header / mobile drawer): /my-fleet?add=1
  useEffect(() => {
    if (searchParams.get("add") === "1" && !loading) {
      openAdd();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading]);

  const openUpdate = (row) => {
    setForm({
      ...emptyForm,
      city: row.city || "",
      radius_km: row.radius_km ?? 200,
      dests: (row.destinations || []).map((d) => d.city),
      minRate: row.min_price ?? null,
      currency: row.currency ?? 2,
      vehicle_id: row.vehicle_id,
    });
    setAvailModal({ mode: "update", vehicle: row });
  };

  const openEditVehicle = (row) => {
    setVForm({
      mark: row.mark === "—" ? "" : row.mark,
      model: row.model,
      government_number: row.plate === "—" ? "" : row.plate,
      body_type: row.body_type_id ?? null,
      max_load_weight: row.max_load_weight ?? null,
      max_load_volume: row.max_load_volume ?? null,
    });
    setEditVehicle(row);
  };

  const saveVehicle = async () => {
    if (!vForm.mark?.trim() || !vForm.government_number?.trim()) {
      message.warning("Укажите марку и госномер");
      return;
    }
    setBusy(true);
    try {
      const body = {
        mark: vForm.mark.trim(),
        model: vForm.model?.trim() || null,
        government_number: vForm.government_number.trim(),
        body_type: vForm.body_type ?? null,
        max_load_weight: vForm.max_load_weight ?? null,
        max_load_volume: vForm.max_load_volume ?? null,
      };
      if (editVehicle.create) {
        await axios.post(`${API}/transports/`, body, { headers: authHeaders() });
      } else {
        await axios.patch(`${API}/transports/${editVehicle.vehicle_id}/`, body, {
          headers: authHeaders(),
        });
      }
      message.success("Машина сохранена");
      setEditVehicle(null);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "Не удалось сохранить машину");
    } finally {
      setBusy(false);
    }
  };

  const saveAvailability = async () => {
    if (!form.city?.trim()) {
      message.warning("Укажите, где сейчас машина");
      return;
    }
    if (form.until && form.from && form.until.isBefore(form.from)) {
      message.warning("«Актуально до» раньше даты «Свободен с»");
      return;
    }
    setBusy(true);
    try {
      const targets =
        availModal.mode === "bulk" ? availModal.ids : [form.vehicle_id];
      if (targets.length === 0 || targets.some((v) => !v)) {
        message.warning("Выберите машину");
        setBusy(false);
        return;
      }
      const payload = {
        city: form.city.trim(),
        // full ISO instants: no day shift in non-UTC time zones
        available_from: form.from ? form.from.toISOString() : undefined,
        available_until: form.until ? form.until.toISOString() : undefined,
        destinations: (form.dests || []).map((c) => ({ city: c })),
        radius_km: form.radius_km,
        comment: form.comment?.trim() || undefined,
      };
      if (form.minRate) {
        payload.min_price = form.minRate;
        payload.currency = form.currency;
      }
      const results = await Promise.all(
        targets.map((vehicle_id) => setTruckAvailable({ ...payload, vehicle_id })),
      );
      setAvailModal(null);
      setSelected([]);
      if (targets.length === 1) {
        // spec §9 / «wow»: show the market right after publishing, with CTAs
        const res = results[0];
        setPublishResult({
          count: res?.matched_loads?.count ?? 0,
          top: res?.matched_loads?.top ?? [],
        });
      } else {
        message.success(`Опубликовано машин: ${targets.length}`);
      }
      load();
    } catch (e) {
      message.error(
        e?.message || e?.response?.data?.detail || "Не удалось опубликовать",
      );
    } finally {
      setBusy(false);
    }
  };

  // ── request card (shared: drawer + requests tab) ──────────────────────
  const renderRequestCard = (m, showTruck = false) => {
    const truck = vehicles.find((v) => v.vehicle_id === m.vehicle_id);
    const decided = m.decided;
    const decMeta = decided ? DECIDED_META[decided.state] || DECIDED_META.closed : null;
    const acting = busyMatch === m.match_id;
    return (
      <div
        key={m.match_id}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "12px 14px",
          marginBottom: 10,
          background: decided ? "#fafafa" : "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <strong style={{ fontSize: 14 }}>
            {m.load.customer || "Заказчик"}
          </strong>
          <span style={{ fontWeight: 700, color: scoreColor(m.score) }}>
            {m.score}% подходит
          </span>
        </div>
        <div style={{ fontSize: 14, marginBottom: 4 }}>
          <strong>
            {m.load.from} → {m.load.to}
          </strong>
          {showTruck && truck && (
            <span style={{ color: "#6b7280", marginLeft: 8, fontSize: 12 }}>
              · {truck.mark} ({truck.plate})
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>
          {m.load.body_type?.name || "—"} · {m.load.weight_t ?? "—"} т
          {m.load.volume_m3 ? ` / ${m.load.volume_m3} м³` : ""} · загрузка{" "}
          {fmtDate(m.load.loading_from)}
          {m.load.loading_to && m.load.loading_to !== m.load.loading_from
            ? `–${fmtDate(m.load.loading_to)}`
            : ""}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {fmtMoney(m.load.price, m.load.currency)}
          </span>
          <span style={{ fontSize: 12, color: "#9aa0a6" }}>
            {m.load.created_minutes_ago < 60
              ? `${m.load.created_minutes_ago} мин назад`
              : `${Math.round(m.load.created_minutes_ago / 60)} ч назад`}
          </span>
        </div>

        {decided ? (
          <div style={{ marginTop: 8 }}>
            <Tag color={decMeta.color}>
              {decMeta.label}
              {decided.amount != null && decided.state === "countered"
                ? `: ${fmtMoney(decided.amount, decided.currency)}`
                : ""}
            </Tag>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <Button
              size="small"
              type="primary"
              loading={acting}
              onClick={() => onAcceptRequest(m)}
            >
              Принять запрос
            </Button>
            <Button size="small" onClick={() => openCounter(m)}>
              Предложить цену
            </Button>
            <Popconfirm
              title="Отклонить этот запрос?"
              okText="Да"
              cancelText="Отмена"
              onConfirm={() => onDecline(m)}
            >
              <Button size="small" danger loading={acting}>
                Отклонить
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
    );
  };

  // ── fleet table columns (wireframe Fleet_Requests_2) ──────────────────
  const strSort = (key) => (x, y) =>
    String(x[key] || "").localeCompare(String(y[key] || ""), "ru");
  const columns = [
    {
      title: "Машина",
      key: "truck",
      sorter: strSort("mark"),
      render: (_, r) => (
        <span>
          <strong>{r.mark}</strong>
          {r.model && r.model !== r.mark ? <span> {r.model}</span> : null}
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            {r.body_type} • {r.max_load_weight ?? "—"} т
          </div>
        </span>
      ),
    },
    { title: "Госномер", dataIndex: "plate", key: "plate", sorter: strSort("plate") },
    { title: "Тип кузова", dataIndex: "body_type", key: "body_type" },
    {
      title: "Где сейчас",
      dataIndex: "city",
      key: "city",
      sorter: strSort("city"),
      render: (v) => v || <span style={{ color: "#c0c4cc" }}>—</span>,
    },
    {
      title: "Куда ищет груз",
      key: "dest",
      sorter: (x, y) =>
        String(x.destinations?.[0]?.city || "").localeCompare(
          String(y.destinations?.[0]?.city || ""),
          "ru",
        ),
      render: (_, r) =>
        (r.destinations || []).length > 0 ? (
          <span>
            {r.destinations.map((d) => d.city).join(", ")}
            {r.destinations[0]?.country && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {r.destinations[0].country}
              </div>
            )}
          </span>
        ) : r.live && r.state !== "idle" ? (
          "Любое"
        ) : (
          "—"
        ),
    },
    {
      title: "Ставка от",
      key: "rate",
      sorter: (x, y) => (x.min_price ?? 0) - (y.min_price ?? 0),
      render: (_, r) =>
        r.min_price != null
          ? `от ${Number(r.min_price).toLocaleString("ru-RU")} ${currencySign(r.currency)}`.trim()
          : "—",
    },
    {
      title: "Статус",
      key: "state",
      render: (_, r) => {
        const m = STATUS_META[r.state];
        return (
          <Tag
            color={m.color}
            title={r.expired ? "Статус истёк — обновите состояние" : undefined}
          >
            {m.label}
          </Tag>
        );
      },
    },
    {
      title: "Запросы",
      key: "requests",
      width: 130,
      render: (_, r) => {
        const list = requestsByVehicle.get(r.vehicle_id) || [];
        const fresh = list.filter((m) => !m.decided).length;
        return (
          <Button
            size="small"
            type={fresh > 0 ? "primary" : "text"}
            ghost={fresh > 0}
            onClick={() => setDrawerVehicle(r)}
            disabled={list.length === 0}
          >
            {fresh > 0
              ? `${fresh} ${plural(fresh, "новый", "новых", "новых")}`
              : "—"}
            {list.length > 0 && <RightOutlined style={{ fontSize: 10 }} />}
          </Button>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="small" type="primary" ghost onClick={() => openUpdate(r)}>
            Обновить
          </Button>
          <Button size="small" onClick={() => openEditVehicle(r)}>
            Изменить
          </Button>
        </div>
      ),
    },
  ];

  const drawerRequests = drawerVehicle
    ? requestsByVehicle.get(drawerVehicle.vehicle_id) || []
    : [];
  const drawerNewCount = drawerRequests.filter((m) => !m.decided).length;

  const statCard = (value, label, accent = "#111827", delta = null) => (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "12px 16px",
      }}
    >
      <div style={{ fontSize: 24, fontWeight: 700, color: accent }}>{value}</div>
      <div style={{ fontSize: 12, color: "#63738a" }}>{label}</div>
      {delta && <div style={{ fontSize: 11, color: "#1e8e5a" }}>{delta}</div>}
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Мой транспорт</h2>
          <div style={{ color: "#63738a", fontSize: 13 }}>
            Управляйте автопарком, отмечайте свободные машины и отвечайте на
            запросы по грузам.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Обновить
          </Button>
          <Button icon={<PlusOutlined />} onClick={openCreateVehicle}>
            Добавить машину
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Добавить свободную машину
          </Button>
        </div>
      </div>

      {liveError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Сервис подбора недоступен — статусы и запросы могут быть неактуальны."
        />
      )}

      {/* stats (mockup iTruck_Fleet_Requests_2) */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {statCard(stats.total, "Всего машин")}
        {statCard(stats.active, "Ищут груз сейчас", "#1e8e5a")}
        {statCard(stats.tomorrow, "Освобождаются завтра", "#2463eb")}
        {statCard(stats.busy, "В рейсе", "#d46b08")}
        {statCard(
          stats.newRequests,
          "Новые запросы",
          stats.newRequests > 0 ? "#2463eb" : "#111827",
          stats.newRequests > 0 ? "требуют ответа" : null,
        )}
      </div>

      {/* tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Button
          type={tab === "fleet" ? "primary" : "default"}
          onClick={() => setTab("fleet")}
        >
          Свободные машины ({stats.active + stats.tomorrow})
        </Button>
        <Badge count={newCount} size="small" offset={[-4, 4]}>
          <Button
            type={tab === "requests" ? "primary" : "default"}
            onClick={() => setTab("requests")}
          >
            Запросы ({requests.length})
          </Button>
        </Badge>
      </div>

      {selected.length > 0 && tab === "fleet" && (
        <div
          style={{
            marginBottom: 12,
            display: "flex",
            gap: 8,
            alignItems: "center",
            background: "#f0f7ff",
            padding: "8px 12px",
            borderRadius: 8,
          }}
        >
          <span style={{ fontWeight: 600 }}>Выбрано: {selected.length}</span>
          <Popconfirm
            title="Перевести выбранные машины в «В рейсе»?"
            onConfirm={() => onBulkStatus("BUSY")}
            okText="Да"
            cancelText="Отмена"
          >
            <Button danger size="small" icon={<ThunderboltOutlined />}>
              В рейсе
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Перевести выбранные машины в «Неактивна»?"
            onConfirm={() => onBulkStatus("INACTIVE")}
            okText="Да"
            cancelText="Отмена"
          >
            <Button size="small">Неактивна</Button>
          </Popconfirm>
          <Button type="primary" size="small" onClick={openBulk}>
            Обновить состояние
          </Button>
          <Button size="small" loading={busy} onClick={onBulkConfirm}>
            Подтвердить всё
          </Button>
          <Button type="text" size="small" onClick={() => setSelected([])}>
            Снять
          </Button>
        </div>
      )}

      {tab === "fleet" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Select
              allowClear
              placeholder="Все статусы"
              style={{ width: 190 }}
              value={fStatus}
              onChange={(v) => { setFStatus(v ?? null); setPage(1); }}
              options={STATUS_OPTIONS}
            />
            <Select
              allowClear
              placeholder="Все типы кузова"
              style={{ width: 170 }}
              value={fBody}
              onChange={(v) => { setFBody(v ?? null); setPage(1); }}
              options={BODY_TYPE_OPTIONS}
            />
            <Select
              allowClear
              showSearch
              placeholder="Город (где сейчас)"
              style={{ width: 190 }}
              value={fCity}
              onChange={(v) => { setFCity(v ?? null); setPage(1); }}
              options={cityOptions}
            />
            <Select
              allowClear
              showSearch
              placeholder="Куда ищет"
              style={{ width: 170 }}
              value={fDest}
              onChange={(v) => { setFDest(v ?? null); setPage(1); }}
              options={destOptions}
            />
            {hasFilters && (
              <Button type="link" onClick={resetFilters}>
                Сбросить
              </Button>
            )}
          </div>
          <Table
            size="middle"
            rowKey="vehicle_id"
            columns={columns}
            dataSource={filtered}
            loading={loading}
            scroll={{ x: 1100 }}
            pagination={{
              current: page,
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: [10, 20],
              locale: { items_per_page: "на странице" },
              showTotal: (total, range) =>
                `Показано ${range[1] - range[0] + 1} из ${total} ${plural(total, "машины", "машин", "машин")}`,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            rowSelection={{
              selectedRowKeys: selected,
              onChange: setSelected,
            }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    hasFilters
                      ? "Машины не найдены. Измените фильтры поиска."
                      : "Машин пока нет"
                  }
                >
                  {hasFilters && <Button onClick={resetFilters}>Сбросить</Button>}
                </Empty>
              ),
            }}
          />
        </>
      )}

      {tab === "requests" && (
        <Spin spinning={loading}>
          {requests.length === 0 ? (
            <Empty
              description="Запросов пока нет"
              style={{ padding: "40px 0" }}
            />
          ) : (
            <div style={{ maxWidth: 640 }}>
              {requests.map((m) => renderRequestCard(m, true))}
            </div>
          )}
        </Spin>
      )}

      {/* per-truck requests drawer (mockup: «Входящие запросы») */}
      <Drawer
        title={
          drawerVehicle
            ? `Входящие запросы — ${drawerVehicle.mark} · ${drawerVehicle.plate}`
            : "Входящие запросы"
        }
        placement="right"
        width={420}
        open={Boolean(drawerVehicle)}
        onClose={() => setDrawerVehicle(null)}
      >
        {drawerVehicle && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <span style={{ fontWeight: 600 }}>
                {drawerNewCount > 0
                  ? `${drawerNewCount} новых запроса`
                  : "Нет новых запросов"}
              </span>
              <span style={{ color: "#6b7280", fontSize: 12 }}>
                {drawerVehicle.body_type} · {drawerVehicle.max_load_weight} т
              </span>
            </div>
            {drawerRequests.length === 0 ? (
              <Empty description="Здесь появятся обращения по выбранной машине" />
            ) : (
              drawerRequests.map((m) => renderRequestCard(m))
            )}
          </>
        )}
      </Drawer>

      {/* counter-offer modal («Предложить цену», ТЗ §8) */}
      <Modal
        title={
          counterTarget
            ? `Предложить цену — ${counterTarget.load.from} → ${counterTarget.load.to}`
            : "Предложить цену"
        }
        open={Boolean(counterTarget)}
        onCancel={() => setCounterTarget(null)}
        onOk={onCounter}
        okText="Отправить"
        cancelText="Отмена"
        confirmLoading={Boolean(counterTarget && busyMatch === counterTarget.match_id)}
      >
        {counterTarget?.load.price != null && (
          <div style={{ marginBottom: 8, color: "#6b7280", fontSize: 13 }}>
            Ставка заказчика: {fmtMoney(counterTarget.load.price, counterTarget.load.currency)}
          </div>
        )}
        <InputNumber
          style={{ width: "100%" }}
          size="large"
          min={1}
          value={counterAmount}
          onChange={setCounterAmount}
          placeholder="Ваша ставка"
        />
      </Modal>

      {/* publish / update availability (wireframe Add_Free_Vehicle §1–2) */}
      <Modal
        title={
          availModal?.mode === "update"
            ? `Обновить состояние — ${availModal.vehicle.mark} ${availModal.vehicle.plate}`
            : availModal?.mode === "bulk"
              ? `Обновить состояние — машин: ${availModal.ids.length}`
              : "Добавить свободную машину"
        }
        open={Boolean(availModal)}
        onCancel={() => setAvailModal(null)}
        onOk={saveAvailability}
        okText="Опубликовать"
        cancelText="Отмена"
        confirmLoading={busy}
        width={640}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {availModal?.mode === "add" && (
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Машина *"
              size="large"
              value={form.vehicle_id}
              onChange={(v) => setF({ vehicle_id: v })}
              options={vehicles.map((v) => ({
                value: v.vehicle_id,
                label: `${v.mark} · ${v.plate} · ${v.body_type}`,
              }))}
            />
          )}

          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
              Где сейчас находится машина *
            </div>
            <Input
              value={form.city}
              onChange={(e) => setF({ city: e.target.value })}
              placeholder="Например, Москва"
              size="large"
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
              Куда ищет груз (города; пусто = любое направление)
            </div>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              size="large"
              tokenSeparators={[","]}
              value={form.dests}
              onChange={(v) => setF({ dests: v })}
              placeholder="Бишкек, Алматы"
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
                Свободен с (дата и время)
              </div>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="DD.MM.YYYY HH:mm"
                style={{ width: "100%" }}
                value={form.from}
                onChange={(v) => setF({ from: v })}
                size="large"
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
                Ставка от (необязательно)
              </div>
              <InputNumber
                style={{ width: "100%" }}
                size="large"
                min={0}
                value={form.minRate}
                onChange={(v) => setF({ minRate: v })}
                placeholder="180000"
              />
            </div>
          </div>
          <Collapse
            ghost
            items={[
              {
                key: "more",
                label: "Дополнительные параметры",
                children: (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
                          Радиус поиска груза
                        </div>
                        <Select
                          style={{ width: "100%" }}
                          value={form.radius_km}
                          onChange={(v) => setF({ radius_km: v })}
                          options={RADIUS_OPTIONS}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
                          Валюта ставки
                        </div>
                        <Select
                          style={{ width: "100%" }}
                          value={form.currency}
                          onChange={(v) => setF({ currency: v })}
                          options={CURRENCIES.map(({ value, label }) => ({ value, label }))}
                        />
                      </div>
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
                        Актуально до
                      </div>
                      <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format="DD.MM.YYYY HH:mm"
                        style={{ width: "100%" }}
                        value={form.until}
                        onChange={(v) => setF({ until: v })}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 4, fontSize: 13, color: "#6b7280" }}>
                        Комментарий для грузоотправителя ({(form.comment || "").length}/1000)
                      </div>
                      <Input.TextArea
                        rows={3}
                        maxLength={1000}
                        value={form.comment}
                        onChange={(e) => setF({ comment: e.target.value })}
                      />
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </Modal>

      {/* after publish: show the market instantly (spec §5/§9) */}
      <Modal
        title="Машина опубликована"
        open={Boolean(publishResult)}
        onCancel={() => setPublishResult(null)}
        footer={<Button onClick={() => setPublishResult(null)}>Закрыть</Button>}
        width={560}
      >
        {publishResult && (
          <>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              {publishResult.count > 0
                ? `Найдено ${publishResult.count} ${plural(publishResult.count, "подходящий груз", "подходящих груза", "подходящих грузов")}`
                : "Пока нет подходящих грузов — пришлём уведомление, когда появится."}
            </div>
            {publishResult.top.map((m) => (
              <div key={m.match_id}>{renderRequestCard(m)}</div>
            ))}
          </>
        )}
      </Modal>

      {/* edit vehicle (wireframe Edit_Vehicle — fields the backend model has) */}
      <Modal
        title={
          editVehicle?.create
            ? "Новая машина (постоянные данные)"
            : `Редактировать машину — ${editVehicle?.mark || ""} ${editVehicle?.plate || ""}`
        }
        open={Boolean(editVehicle)}
        onCancel={() => setEditVehicle(null)}
        onOk={saveVehicle}
        okText={editVehicle?.create ? "Создать машину" : "Сохранить изменения"}
        cancelText="Отмена"
        confirmLoading={busy}
        destroyOnClose
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Input placeholder="Марка *" value={vForm.mark}
            onChange={(e) => setVForm({ ...vForm, mark: e.target.value })} />
          <Input placeholder="Модель" value={vForm.model}
            onChange={(e) => setVForm({ ...vForm, model: e.target.value })} />
          <Input placeholder="Госномер *" value={vForm.government_number}
            onChange={(e) => setVForm({ ...vForm, government_number: e.target.value })} />
          <Select placeholder="Тип кузова" options={BODY_TYPE_OPTIONS} value={vForm.body_type}
            onChange={(v) => setVForm({ ...vForm, body_type: v })} />
          <InputNumber style={{ width: "100%" }} min={0} placeholder="Грузоподъёмность, т"
            value={vForm.max_load_weight}
            onChange={(v) => setVForm({ ...vForm, max_load_weight: v })} />
          <InputNumber style={{ width: "100%" }} min={0} placeholder="Объём, м³"
            value={vForm.max_load_volume}
            onChange={(v) => setVForm({ ...vForm, max_load_volume: v })} />
        </div>
      </Modal>
    </div>
  );
};

export default MyFleet;
